import { useState, useEffect, useCallback } from "react";
import { apiCall } from "../api";
import { Modal } from "./common/Modal";

/**
 * Administration -> API Keys.
 *
 * The raw secret is held in React state only, for as long as the "created"
 * dialog is open, and is dropped when that dialog closes. It is deliberately
 * never written to localStorage or sessionStorage: anything placed there
 * survives a reload and is readable by any script on the page, which is exactly
 * what "shown once" is meant to prevent. After the dialog closes the server has
 * no way to show it again either — only the HMAC digest was stored.
 */
export default function APIKeysAdmin() {
  const [keys, setKeys] = useState([]);
  const [scopeCatalog, setScopeCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [chosenScopes, setChosenScopes] = useState([]);
  const [creating, setCreating] = useState(false);

  const [newKey, setNewKey] = useState(null);   // { api_key, prefix, name, ... }
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, cat] = await Promise.all([
        apiCall("get", "/admin/api-keys"),
        apiCall("get", "/admin/api-keys/scopes"),
      ]);
      setKeys(list.data || []);
      setScopeCatalog(cat.scopes || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (s) =>
    setChosenScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  async function create() {
    if (!name.trim()) { setError("Give the key a name so it can be identified later."); return; }
    if (chosenScopes.length === 0) { setError("Select at least one scope."); return; }
    setCreating(true);
    setError("");
    try {
      const res = await apiCall("post", "/admin/api-keys", {
        name: name.trim(), description: description.trim(), scopes: chosenScopes,
      });
      setShowCreate(false);
      setName(""); setDescription(""); setChosenScopes([]);
      setNewKey(res);          // holds the raw secret — this state only
      setCopied(false);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create the key.");
    } finally {
      setCreating(false);
    }
  }

  async function revoke() {
    try {
      await apiCall("post", `/admin/api-keys/${revokeTarget.key_id}/revoke`);
      setRevokeTarget(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not revoke the key.");
      setRevokeTarget(null);
    }
  }

  async function doDelete() {
    try {
      await apiCall("delete", `/admin/api-keys/${deleteTarget.key_id}`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not delete the key.");
      setDeleteTarget(null);
    }
  }

  async function openAudit(k) {
    setAuditFor(k);
    setAuditRows([]);
    try {
      const res = await apiCall("get", `/admin/api-keys/${k.key_id}/audit?limit=200`);
      setAuditRows(res.data || []);
    } catch {
      setAuditRows([]);
    }
  }

  const fmt = (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—");

  return (
    <div data-testid="api-keys-admin">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">API Keys</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Keys let an external backend (for example MRM) read Maintenance data over
            <code className="mx-1 px-1 bg-gray-100 rounded">X-API-Key</code>. They are read-only
            and do not affect user logins.
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setError(""); }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                data-testid="create-api-key-btn">+ Create API Key</button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded"
             data-testid="api-keys-error">{error}</div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center border border-dashed rounded">
          No API keys yet. Create one to let an external system read Maintenance data.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Key</th>
                <th className="text-left px-3 py-2 font-medium">Scopes</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="text-left px-3 py-2 font-medium">Last used</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id} className="border-t" data-testid={`api-key-row-${k.key_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{k.name}</div>
                    {k.description && <div className="text-xs text-gray-500">{k.description}</div>}
                  </td>
                  {/* Masked prefix only — the full secret is not retrievable. */}
                  <td className="px-3 py-2"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{k.prefix}…</code></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <span key={s} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {fmt(k.created_at)}
                    {k.created_by_name && <div className="text-gray-400">by {k.created_by_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{fmt(k.last_used_at)}</td>
                  <td className="px-3 py-2">
                    {k.status === "active"
                      ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                      : <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded" title={`Revoked ${fmt(k.revoked_at)}`}>Revoked</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openAudit(k)} className="text-xs text-gray-600 hover:text-gray-900 mr-3"
                            data-testid={`audit-${k.key_id}`}>History</button>
                    {k.status === "active" ? (
                      <button onClick={() => setRevokeTarget(k)} className="text-xs text-amber-600 hover:text-amber-800"
                              data-testid={`revoke-${k.key_id}`}>Revoke</button>
                    ) : (
                      <button onClick={() => setDeleteTarget(k)} className="text-xs text-red-600 hover:text-red-800"
                              data-testid={`delete-${k.key_id}`}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="Create API Key" onClose={() => setShowCreate(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. MRM Integration"
                     className="w-full border rounded px-3 py-2 text-sm" data-testid="api-key-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)}
                     placeholder="What will use this key?"
                     className="w-full border rounded px-3 py-2 text-sm" data-testid="api-key-desc" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scopes <span className="font-normal text-gray-500">— all read-only</span>
              </label>
              <div className="space-y-1.5 max-h-64 overflow-y-auto border rounded p-2">
                {scopeCatalog.map((s) => (
                  <label key={s.scope} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input type="checkbox" checked={chosenScopes.includes(s.scope)}
                           onChange={() => toggleScope(s.scope)} className="mt-0.5"
                           data-testid={`scope-${s.scope}`} />
                    <span>
                      <code className="text-xs font-medium text-blue-700">{s.scope}</code>
                      <span className="block text-xs text-gray-500">{s.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border rounded">Cancel</button>
              <button onClick={create} disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      data-testid="confirm-create-api-key">{creating ? "Creating…" : "Generate Key"}</button>
            </div>
          </div>
        </Modal>
      )}

      {newKey && (
        <Modal title="API Key Created" onClose={() => { setNewKey(null); setCopied(false); }}>
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-900 font-medium"
                 data-testid="copy-once-warning">
              Copy this key now. It will not be shown again.
            </div>
            <div className="flex gap-2">
              <code className="flex-1 block bg-gray-900 text-green-300 text-xs p-3 rounded break-all"
                    data-testid="new-api-key-value">{newKey.api_key}</code>
              <button
                onClick={() => { navigator.clipboard?.writeText(newKey.api_key); setCopied(true); }}
                className="px-3 py-2 bg-gray-800 text-white text-xs rounded self-start"
                data-testid="copy-api-key">{copied ? "Copied" : "Copy"}</button>
            </div>
            <div className="text-xs text-gray-600 space-y-1">
              <div><span className="text-gray-500">Name:</span> {newKey.name}</div>
              <div><span className="text-gray-500">Scopes:</span> {newKey.scopes.join(", ")}</div>
              <div className="pt-2">Send it in the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header:</div>
              <pre className="bg-gray-50 border rounded p-2 overflow-x-auto">{`curl -H "X-API-Key: <key>" \\
  ${window.location.origin}/api/external/maintenance/machines`}</pre>
            </div>
            <div className="flex justify-end pt-1">
              <button onClick={() => { setNewKey(null); setCopied(false); }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded"
                      data-testid="close-new-key">Done</button>
            </div>
          </div>
        </Modal>
      )}

      {revokeTarget && (
        <Modal title="Revoke API Key" onClose={() => setRevokeTarget(null)}>
          <p className="text-sm text-gray-700">
            Revoke <strong>{revokeTarget.name}</strong> (<code className="text-xs">{revokeTarget.prefix}…</code>)?
            It stops working immediately and anything using it will start receiving 401.
            The record is kept for audit.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setRevokeTarget(null)} className="px-4 py-2 text-sm border rounded">Cancel</button>
            <button onClick={revoke} className="px-4 py-2 bg-amber-600 text-white text-sm rounded"
                    data-testid="confirm-revoke">Revoke</button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="Delete API Key" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-700">
            Permanently delete the record for <strong>{deleteTarget.name}</strong>? The key is already
            revoked, so this changes no access. Its audit history is retained.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border rounded">Cancel</button>
            <button onClick={doDelete} className="px-4 py-2 bg-red-600 text-white text-sm rounded"
                    data-testid="confirm-delete">Delete</button>
          </div>
        </Modal>
      )}

      {auditFor && (
        <Modal title={`History — ${auditFor.name}`} onClose={() => setAuditFor(null)} size="large">
          {auditRows.length === 0 ? (
            <p className="text-sm text-gray-500">No recorded events yet.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Event</th>
                    <th className="text-left px-2 py-1.5">Endpoint / scope</th>
                    <th className="text-left px-2 py-1.5">Result</th>
                    <th className="text-left px-2 py-1.5">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-2 py-1.5 whitespace-nowrap">{fmt(r.timestamp)}</td>
                      <td className="px-2 py-1.5">{r.event}</td>
                      <td className="px-2 py-1.5"><code>{r.endpoint || "—"}</code></td>
                      <td className="px-2 py-1.5">
                        {r.success ? <span className="text-green-700">ok</span> : <span className="text-red-600">failed</span>}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500">{r.detail || r.actor || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
